<template>
  <Dialog
    v-model:visible="localVisible"
    modal
    :closable="true"
    :draggable="false"
    :style="{ width: '90vw', maxWidth: '450px' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    appendTo="body"
    :blockScroll="true"
  >
    <template #header>
      <div class="flex align-items-center gap-3">
        <i :class="iconClass" :style="{ fontSize: '2rem', color: iconColor }"></i>
        <h3 class="m-0">{{ modalTitle }}</h3>
      </div>
    </template>

    <div class="modal-content">
      <p class="text-lg mb-3">{{ modalMessage }}</p>
      
      <div v-if="showInstructions" class="instructions-box p-4 border-round-lg mt-4">
        <h4 class="text-base mb-3 flex align-items-center gap-2">
          <i class="pi pi-info-circle"></i>
          Cara Menggunakan:
        </h4>
        <ol class="pl-3 m-0">
          <li class="mb-2">Scan tiket di halaman <strong>Scan Ticket</strong></li>
          <li class="mb-2">Klik tombol <strong>Mulai Spin!</strong></li>
          <li>Putar <strong>Spin Wheel</strong> untuk memenangkan hadiah</li>
        </ol>
      </div>

      <div v-if="showSuccessMessage" class="success-box p-4 border-round-lg mt-4">
        <div class="flex align-items-center gap-3 mb-3">
          <i class="pi pi-trophy" style="font-size: 2.5rem; color: var(--primary-color);"></i>
          <div>
            <h4 class="m-0 mb-1">Selamat!</h4>
            <p class="m-0 text-sm text-color-secondary">Anda telah mendapatkan hadiah</p>
          </div>
        </div>
        <p class="text-center m-0">
          <i class="pi pi-arrow-down mr-2"></i>
          Scan tiket baru untuk mencoba lagi
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-end gap-2 w-full">
        <Button
          v-if="showScanButton"
          label="Scan Tiket"
          icon="pi pi-qrcode"
          @click="handleScanClick"
          severity="success"
          size="large"
          class="flex-1"
          :style="{ fontSize: '1.1rem', padding: '0.75rem 1.5rem' }"
        />
        <Button
          v-if="showCloseButton"
          :label="closeLabel"
          :icon="closeIcon"
          @click="handleClose"
          :severity="closeSeverity"
          size="large"
          class="flex-1"
          :style="{ fontSize: '1.1rem', padding: '0.75rem 1.5rem' }"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

interface Props {
  visible: boolean;
  type: 'no-ticket' | 'spin-complete' | 'spinning';
  title?: string;
  message?: string;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  type: 'no-ticket',
  title: '',
  message: ''
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'scan-click': [];
  'close': [];
}>();

const localVisible = ref(props.visible);

watch(() => props.visible, (newVal) => {
  localVisible.value = newVal;
});

watch(localVisible, (newVal) => {
  emit('update:visible', newVal);
});

// Computed properties based on modal type
const iconClass = computed(() => {
  switch (props.type) {
    case 'no-ticket':
      return 'pi pi-lock';
    case 'spin-complete':
      return 'pi pi-check-circle';
    case 'spinning':
      return 'pi pi-spin pi-spinner';
    default:
      return 'pi pi-info-circle';
  }
});

const iconColor = computed(() => {
  switch (props.type) {
    case 'no-ticket':
      return '#f59e0b';
    case 'spin-complete':
      return '#10b981';
    case 'spinning':
      return '#3b82f6';
    default:
      return '#6366f1';
  }
});

const modalTitle = computed(() => {
  if (props.title) return props.title;
  
  switch (props.type) {
    case 'no-ticket':
      return '🔒 Tiket Diperlukan';
    case 'spin-complete':
      return '🎉 Spin Selesai';
    case 'spinning':
      return '⏳ Mohon Tunggu';
    default:
      return 'Informasi';
  }
});

const modalMessage = computed(() => {
  if (props.message) return props.message;
  
  switch (props.type) {
    case 'no-ticket':
      return 'Anda belum memiliki tiket aktif. Silakan scan tiket terlebih dahulu untuk dapat memutar spin wheel.';
    case 'spin-complete':
      return 'Tiket Anda telah berhasil digunakan untuk spin ini.';
    case 'spinning':
      return 'Spin sedang berlangsung, tunggu hingga selesai.';
    default:
      return '';
  }
});

const showInstructions = computed(() => props.type === 'no-ticket');
const showSuccessMessage = computed(() => props.type === 'spin-complete');
const showScanButton = computed(() => props.type === 'no-ticket' || props.type === 'spin-complete');
const showCloseButton = computed(() => props.type === 'spinning' || props.type === 'spin-complete');

const closeLabel = computed(() => {
  return props.type === 'spin-complete' ? 'Tutup' : 'Mengerti';
});

const closeIcon = computed(() => {
  return props.type === 'spin-complete' ? 'pi pi-times' : 'pi pi-check';
});

const closeSeverity = computed(() => {
  return props.type === 'spin-complete' ? 'secondary' : 'info';
});

const handleScanClick = () => {
  emit('scan-click');
  localVisible.value = false;
};

const handleClose = () => {
  emit('close');
  localVisible.value = false;
};
</script>

<style scoped lang="scss">
.modal-content {
  padding: 1rem 0;
}

.instructions-box {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #fbbf24;
  animation: slideInUp 0.3s ease;

  h4 {
    color: #92400e;
    font-weight: 600;
  }

  ol {
    color: #78350f;
    
    li {
      line-height: 1.8;
      
      strong {
        color: #92400e;
        font-weight: 600;
      }
    }
  }
}

.success-box {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 2px solid #10b981;
  animation: slideInUp 0.3s ease;

  h4 {
    color: #065f46;
    font-weight: 600;
  }

  p {
    color: #064e3b;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

:deep(.p-dialog) {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-radius: 1rem;
  animation: modalFadeIn 0.3s ease;
}

:deep(.p-dialog-header) {
  border-radius: 1rem 1rem 0 0;
  padding: 1.5rem;
}

:deep(.p-dialog-content) {
  padding: 1.5rem;
}

:deep(.p-dialog-footer) {
  border-radius: 0 0 1rem 1rem;
  padding: 1rem 1.5rem;
}

// Fix modal backdrop to be full screen
:deep(.p-dialog-mask) {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  backdrop-filter: blur(8px) !important;
  background-color: rgba(0, 0, 0, 0.6) !important;
  z-index: 9999 !important;
  overflow: auto !important;
}

:deep(.p-dialog) {
  z-index: 10000 !important;
  margin: 0 !important;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
